import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Lock, Loader2, ShieldCheck, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { blClient, BL_ADMIN_TOKEN_KEY } from "./client";
import { BL_HERO_LOGO } from "./assets";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function BLLogin({ onAuth }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [adminOpen, setAdminOpen] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await blClient.post("/blackleaf/auth", { passcode: pw });
      onAuth(r.data.token);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Try again");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 py-10 black-leaf-theme" data-testid="bl-login-page">
      <Link to="/" className="self-start mb-6 inline-flex items-center gap-1 text-sm underline" style={{ color: "#D9B36A" }} data-testid="bl-back"><ArrowLeft size={14}/> Back</Link>
      <div className="bl-card w-full max-w-md text-center p-8 sm:p-10" data-testid="bl-login-card">
        <div className="bl-kicker">A private community log</div>
        <div className="mt-4 mb-1 flex justify-center"><BLLogo size="lg"/></div>
        <p className="font-display italic text-bl-soft mt-3">For the keepers of dark-foliage dahlias.</p>
        <form onSubmit={submit} className="mt-7 space-y-3 text-left">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.2em] text-bl-soft">Society passphrase</span>
            <input type="password" className="bl-field mt-1.5" placeholder="Shared with the group"
              value={pw} onChange={(e) => setPw(e.target.value)} data-testid="bl-passcode" autoComplete="current-password" required/>
          </label>
          {err && <div className="text-sm text-bl-rose bg-bl-rose/10 px-3 py-2 rounded-lg border border-bl-rose/30" data-testid="bl-error">{err}</div>}
          <button type="submit" className="bl-btn w-full" disabled={busy} data-testid="bl-submit">
            {busy ? <Loader2 size={16} className="animate-spin"/> : <Lock size={14}/>}
            {busy ? "Opening…" : "Enter"}
          </button>
        </form>
        <button
          type="button"
          onClick={() => setAdminOpen(true)}
          className="mt-5 inline-flex items-center gap-1.5 text-xs"
          style={{ color: "rgba(217,179,106,0.7)", letterSpacing: "0.18em", textTransform: "uppercase", fontFamily: "Playfair Display, serif" }}
          data-testid="bl-admin-link"
        >
          <ShieldCheck size={11}/> Society admin sign-in
        </button>
      </div>
      {adminOpen && <AdminSignIn onClose={() => setAdminOpen(false)} onAuthBoth={(socToken) => { onAuth(socToken); }} />}
    </div>
  );
}

function AdminSignIn({ onClose, onAuthBoth }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      // 1. Black Leaf admin login → own admin token
      const a = await axios.post(`${BACKEND}/api/blackleaf/admin/login`, { email, password });
      localStorage.setItem(BL_ADMIN_TOKEN_KEY, a.data.token);
      // 2. Mint a society token using the admin token (no passphrase needed)
      const b = await axios.post(`${BACKEND}/api/blackleaf/admin/society-token`, {}, { headers: { Authorization: `Bearer ${a.data.token}` } });
      toast.success(`Welcome back, ${a.data.admin?.name || "admin"}`);
      onAuthBoth(b.data.token);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Couldn't sign in");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="bl-admin-modal">
      <div className="absolute inset-0 bg-black/75" onClick={onClose}/>
      <div className="bl-card relative w-full max-w-md p-6 sm:p-8 text-left">
        <button onClick={onClose} className="absolute top-3 right-3 bl-btn-ghost"><X size={12}/></button>
        <div className="bl-kicker mb-1">Society admin</div>
        <h3 className="bl-h2 mb-4">Admin sign-in</h3>
        <p className="text-xs text-bl-soft italic mb-4">Approve listings, edit notices, invite other admins.</p>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-bl-soft">Email</span>
            <input type="email" className="bl-field mt-1.5" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" data-testid="bl-admin-email"/>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-bl-soft">Password</span>
            <input type="password" className="bl-field mt-1.5" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" data-testid="bl-admin-password"/>
          </label>
          {err && <div className="text-sm text-bl-rose bg-bl-rose/10 px-3 py-2 rounded-lg border border-bl-rose/30" data-testid="bl-admin-error">{err}</div>}
          <button type="submit" className="bl-btn w-full" disabled={busy} data-testid="bl-admin-submit">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <ShieldCheck size={14}/>}
            {busy ? "Signing in…" : "Sign in as admin"}
          </button>
        </form>
      </div>
    </div>
  );
}

export function BLLogo({ size = "lg" }) {
  if (size === "sm") {
    return (
      <span className="bl-seal-row" data-testid="bl-logo">
        <img src={BL_HERO_LOGO} alt="Black Leaf Dahlia Society" className="bl-seal bl-seal-sm" />
      </span>
    );
  }
  return (
    <div className="bl-seal-wrap" data-testid="bl-logo">
      <img src={BL_HERO_LOGO} alt="Black Leaf Dahlia Society" className="bl-seal bl-seal-lg" />
    </div>
  );
}
